import type { IActiveWorkout } from '@/hooks/useAppState';
import { readWorkoutDraft, writeWorkoutDraft, clearWorkoutDraft, workoutDraftKey, type WorkoutDraft } from './workoutDraft';

export const WORKOUT_DRAFT_CHANGED = 'lianke:workout-draft-changed';
export const workoutExecutionLockName = (owner: string) => 'lianke:workout-execution:' + owner;
const legacyKey = (owner: string) => `lianke:workout-tab-draft:${owner}`;
function notify(owner: string) {
  window.dispatchEvent(new CustomEvent(WORKOUT_DRAFT_CHANGED, { detail: owner }));
}
export function saveBrowserWorkoutDraft(draft: WorkoutDraft): boolean {
  let durable = false;
  try {
    const serialized = JSON.stringify(draft);
    const existing = window.localStorage.getItem(workoutDraftKey(draft.owner));
    if (existing === serialized) return true;
    if (existing) {
      let previous: WorkoutDraft | null = null;
      try { previous = JSON.parse(existing) as WorkoutDraft; } catch { /* Replace a corrupt draft with this valid snapshot. */ }
      if (previous && (previous.startTimestamp > draft.startTimestamp
        || (previous.startTimestamp === draft.startTimestamp && previous.finishedRecordId && !draft.finishedRecordId))) return false;
    }
    durable = writeWorkoutDraft(window.localStorage, draft);
  } catch { /* Fall back to per-tab storage when local persistence is blocked. */ }
  if (!durable) {
    try { writeWorkoutDraft(window.sessionStorage, draft); } catch { /* Both stores may be blocked. */ }
  }
  notify(draft.owner);
  return durable;
}
export function readBrowserWorkoutDraft(owner: string, active: IActiveWorkout, ttl: number): WorkoutDraft | null {
  try {
    if (window.localStorage.getItem(workoutDraftKey(owner)) !== null) {
      return readWorkoutDraft(window.localStorage, owner, active, ttl);
    }
  } catch { /* Try the current tab below. */ }
  try {
    let transient = readWorkoutDraft(window.sessionStorage, owner, active, ttl);
    // Upgrade the previous version without losing an ongoing workout in this tab.
    if (!transient) {
      const raw = window.sessionStorage.getItem(legacyKey(owner));
      if (raw) {
        const legacyStorage = {
          getItem: () => raw, setItem: (_key: string, value: string) => window.sessionStorage.setItem(legacyKey(owner), value),
          removeItem: () => window.sessionStorage.removeItem(legacyKey(owner)),
        };
        transient = readWorkoutDraft(legacyStorage, owner, active, ttl);
      }
    }
    try {
      if (transient && writeWorkoutDraft(window.localStorage, transient)) {
        window.sessionStorage.removeItem(legacyKey(owner));
        clearWorkoutDraft(window.sessionStorage, owner);
      }
    } catch { /* Keep the current tab draft when local persistence is blocked. */ }
    return transient;
  } catch { return null; }
}
export function clearBrowserWorkoutDraft(owner: string) {
  try { clearWorkoutDraft(window.localStorage, owner); } catch { /* Storage may be blocked. */ }
  try {
    clearWorkoutDraft(window.sessionStorage, owner);
    window.sessionStorage.removeItem(legacyKey(owner));
  } catch { /* Storage may be blocked. */ }
  notify(owner);
}

export async function performWorkoutDraftAction(owner: string, action: () => void): Promise<boolean> {
  if (!navigator.locks) { action(); return true; }
  return navigator.locks.request(workoutExecutionLockName(owner), { ifAvailable: true }, lock => {
    if (!lock) return false;
    action(); return true;
  });
}
